/**
 * Legal Documents List View
 *
 * Edit Terms of Service and Privacy Policy content.
 * Documents are stored in the database and served to mobile app users.
 */

import { useState } from 'react';
import toast from 'react-hot-toast';
import { DictionariesNavigation } from '../components/DictionariesNavigation';
import {
  useLegalDocuments,
  useUpdateLegalDocument,
  type LegalDocument,
} from '../services/legalDocumentsApi';

function formatDocumentType(type: string): string {
  if (type === 'terms') return 'Terms of Service';
  if (type === 'privacy') return 'Privacy Policy';
  return type;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

interface DocumentEditorProps {
  document: LegalDocument;
}

function DocumentEditor({ document }: DocumentEditorProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [content, setContent] = useState(document.content);
  const [version, setVersion] = useState(document.version);
  const [effectiveDate, setEffectiveDate] = useState(document.effectiveDate);
  const updateMutation = useUpdateLegalDocument();

  const handleEdit = () => {
    setContent(document.content);
    setVersion(document.version);
    setEffectiveDate(document.effectiveDate);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({
        documentType: document.documentType,
        request: { content, version, effectiveDate },
      });
      toast.success(`${formatDocumentType(document.documentType)} updated`);
      setIsEditing(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            {formatDocumentType(document.documentType)}
          </h2>
          <div className="mt-1 flex items-center gap-4 text-sm text-gray-500">
            <span>Version: {document.version}</span>
            <span>Effective: {formatDate(document.effectiveDate)}</span>
            {document.updatedAt && <span>Updated: {formatDate(document.updatedAt)}</span>}
          </div>
        </div>
        {!isEditing && (
          <button
            onClick={handleEdit}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
          >
            Edit
          </button>
        )}
      </div>

      {/* Content */}
      <div className="px-6 py-4">
        {isEditing ? (
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="w-40">
                <label
                  htmlFor={`version-${document.documentType}`}
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Version
                </label>
                <input
                  id={`version-${document.documentType}`}
                  type="text"
                  value={version}
                  onChange={(e) => setVersion(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
              <div className="w-48">
                <label
                  htmlFor={`date-${document.documentType}`}
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Effective Date
                </label>
                <input
                  id={`date-${document.documentType}`}
                  type="date"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
            </div>
            <div>
              <label
                htmlFor={`content-${document.documentType}`}
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Content (Markdown)
              </label>
              <textarea
                id={`content-${document.documentType}`}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={20}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-mono"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleSave}
                disabled={updateMutation.isPending}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {updateMutation.isPending ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={handleCancel}
                disabled={updateMutation.isPending}
                className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans max-h-64 overflow-y-auto">
            {document.content}
          </pre>
        )}
      </div>
    </div>
  );
}

export function LegalDocumentsListView() {
  const { data: documents, isLoading, error } = useLegalDocuments();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">Error loading legal documents</p>
          <p className="text-gray-600">
            {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <DictionariesNavigation />

      <div className="mb-6">
        <h1 className="text-3xl font-bold">Legal Documents</h1>
        <p className="text-gray-600 mt-2">
          Manage Terms of Service and Privacy Policy displayed to users in the mobile app.
        </p>
      </div>

      <div className="space-y-6">
        {documents?.map((doc) => (
          <DocumentEditor key={doc.documentType} document={doc} />
        ))}
      </div>

      {documents?.length === 0 && (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <p className="text-gray-600 text-lg">No legal documents found in the database.</p>
        </div>
      )}
    </div>
  );
}
